import Image from 'next/image';
import { format } from 'date-fns';

export interface SermonCardProps {
  title: string;
  date: string;
  link: string;
  thumbnail: string | null;
}

export function SermonCard({ title, date, link, thumbnail }: SermonCardProps) {
  const formattedDate = format(new Date(date), 'MMM d, yyyy');

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="group block w-80 flex-shrink-0 bg-background border border-border overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 hover:border-accent/30"
      aria-label={`View sermon: ${title}`}
    >
      <div className="relative w-full h-48 bg-muted overflow-hidden">
        {thumbnail ? (
          <Image
            src={thumbnail}
            alt={title}
            fill
            unoptimized
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="320px"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const placeholder = e.currentTarget.parentElement?.querySelector('.sermon-placeholder');
              if (placeholder) {
                (placeholder as HTMLElement).style.display = 'flex';
              }
            }}
          />
        ) : null}

        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg transform scale-75 group-hover:scale-100 transition-transform duration-300">
            <svg className="w-6 h-6 text-primary ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>

        <div
          className={`sermon-placeholder absolute inset-0 ${thumbnail ? 'hidden' : 'flex'} flex-col items-center justify-center bg-gradient-to-br from-muted via-muted/80 to-muted/60`}
        >
          <svg
            className="w-16 h-16 text-muted-foreground/20 mb-3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-xs font-medium tracking-wider uppercase text-muted-foreground/40">
            Sermon Message
          </span>
        </div>
      </div>

      <div className="p-6 space-y-3">
        {/* Date badge */}
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent" />
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">
            {formattedDate}
          </p>
        </div>

        {/* Title */}
        <h3 className="font-serif font-semibold text-lg leading-snug line-clamp-2 group-hover:text-accent transition-colors duration-300">
          {title}
        </h3>

        {/* Watch link */}
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground group-hover:text-accent transition-colors duration-300 pt-1">
          <span>Watch Now</span>
          <svg
            className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
      </div>
    </a>
  );
}

export function SermonCardSkeleton() {
  return (
    <div className="w-80 flex-shrink-0 border border-border bg-background overflow-hidden">
      <div className="w-full h-48 bg-muted animate-pulse" />
      <div className="p-6 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-muted animate-pulse" />
          <div className="h-3 bg-muted rounded w-20 animate-pulse" />
        </div>
        <div className="space-y-2">
          <div className="h-5 bg-muted rounded animate-pulse" />
          <div className="h-5 bg-muted rounded w-4/5 animate-pulse" />
        </div>
        <div className="h-4 bg-muted rounded w-24 animate-pulse" />
      </div>
    </div>
  );
}
